import { createFileRoute, Navigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";

import i18n from "@/app/i18n";
import { validateAuthToken } from "@/guards/validateAuthToken";
import { validateActiveUser } from "@/guards/validateActiveUser";
import { getLocalizedBirthdate } from "@/lib/utils";

import { SectionRoute } from "@/components/shared/SectionRoute";
import { Separator } from "@/components/ui/separator";
import { BasicPersonalInformation } from "@/components/profile/sections/BasicPersonalInformation";
import { AdditionaPersonalInformation } from "@/components/profile/sections/AdditionalPersonalInformation";
import { ProfessionalData } from "@/components/profile/sections/ProfessionalData";
import { ClinicsInformation } from "@/components/profile/sections/clinics/ClinicsInformation";
import { useAppSelector } from "@/app/store";
import { CountryDTO } from "@/app/api/country/types";
import { ESpecialty } from "@/app/types/enumerations";
import { SkeletonPatientProfile } from "@/components/shared/skeletons/SkeletonPatientProfile";

export const Route = createFileRoute("/dashboard/profile/")({
  beforeLoad: () => {
    validateAuthToken();
    validateActiveUser();
  },
  component: ProfilePage,
});

function ProfilePage() {
  const { t } = useTranslation();
  const { status, data } = useAppSelector((state) => state.provider);

  if (status === "failed") {
    return <Navigate to="/dashboard/error/generic-error" />;
  }

  if (status === "idle" || status === "loading") {
    return (
      <div className="mb-8 max-w-7xl mx-auto">
        <SectionRoute
          route_one={t("routes.dashboard")}
          route_two={t("routes.profile")}
        />
        <SkeletonPatientProfile />
      </div>
    );
  }

  const { email, personalData, professionalData } = data;
  const birthdate = getLocalizedBirthdate(
    personalData.birthdate,
    i18n.language
  );
  const specialty =
    professionalData?.specialty === ESpecialty.OTHER
      ? professionalData?.otherSpecialty
      : professionalData?.specialty;

  return (
    <article className="mb-8 max-w-7xl mx-auto">
      <SectionRoute
        route_one={t("routes.dashboard")}
        route_two={t("routes.profile")}
      />
      <div className="bg-white rounded-xl border border-neutral-100 min-h-full pb-8">
        <div className="flex flex-col gap-4">
          <BasicPersonalInformation
            name={personalData?.firstname}
            email={email}
            id={personalData?.identification.number}
          />
          <Separator />
          <AdditionaPersonalInformation
            phone={personalData?.phoneNumber}
            country={(personalData?.country as CountryDTO)?.name}
            birthdate={birthdate}
          />
          <Separator />
          <ProfessionalData
            specialty={specialty as string}
            institution={professionalData?.educationalInstitution as string}
            license={professionalData?.medicalLicense}
            title={professionalData?.professionalTitle as string}
          />
          <Separator />
          <ClinicsInformation />
        </div>
      </div>
      <div className="h-14"></div>
    </article>
  );
}
